/**
 * API configuration
 * @module apiConfig
 */

/**
 * Get runtime configuration from window.__APP_CONFIG__
 * This allows production deployments to inject config without rebuilding
 * @returns {Object|null} Runtime config object or null
 */
const getRuntimeConfig = () => {
  if (typeof window !== 'undefined' && window.__APP_CONFIG__) {
    return window.__APP_CONFIG__
  }
  return null
}

/**
 * Get a config value with fallback priority:
 * 1. Runtime config (window.__APP_CONFIG__) - highest priority
 * 2. Environment variable (import.meta.env.VITE_*)
 * 3. Development defaults (if in dev mode)
 * @param {string} key - Config key (e.g., 'VITE_API_BASE_URL')
 * @param {string} runtimeKey - Optional runtime config key (defaults to key without VITE_ prefix)
 * @returns {string|undefined} Config value
 */
const getConfigValue = (key, runtimeKey = null) => {
  const runtimeConfig = getRuntimeConfig()
  const runtimeKeyName = runtimeKey || key.replace('VITE_', '')

  // Priority 1: Runtime config
  if (runtimeConfig?.[runtimeKeyName]) {
    return runtimeConfig[runtimeKeyName]
  }

  // Priority 2: Environment variable
  return import.meta.env[key]
}

/**
 * Base URL for the API
 * @type {string}
 *
 * Requirement 4: Environment Variables
 * - In production: Can be set via window.__APP_CONFIG__.API_BASE_URL (runtime) or VITE_API_BASE_URL (build-time)
 * - In development: Falls back to localhost for convenience
 *
 * Note: When accessing the app from a network IP (not localhost), you must set
 * VITE_API_BASE_URL to the backend server address (e.g., http://54.215.63.102:8000)
 * because localhost:8000 will refer to the client machine, not the server.
 */
const getApiBaseUrl = () => {
  // Check runtime config first, then environment variable
  const apiBaseUrl = getConfigValue('VITE_API_BASE_URL', 'API_BASE_URL')

  if (apiBaseUrl) {
    return apiBaseUrl
  }

  // In development, default to localhost (assumes backend is also on localhost)
  if (import.meta.env.DEV) {
    const defaultUrl = 'http://localhost:8000'

    // Warn if accessing from network IP but API URL is still localhost
    if (typeof window !== 'undefined' && window.location) {
      const currentOrigin = window.location.origin
      const isNetworkAccess =
        !currentOrigin.includes('localhost') &&
        !currentOrigin.includes('127.0.0.1') &&
        (currentOrigin.includes('192.168.') ||
          currentOrigin.includes('10.') ||
          currentOrigin.match(/^http:\/\/\d+\.\d+\.\d+\.\d+:/))

      if (isNetworkAccess) {
        console.warn(
          '⚠️ Network Access Detected: API URL is set to localhost but you are accessing from a network IP.\n' +
            `Current origin: ${currentOrigin}\n` +
            'API calls will fail because localhost refers to the client machine, not the server.\n' +
            'Solution: Set VITE_API_BASE_URL=http://54.215.63.102:8000 in your .env file.\n' +
            'See docs/ENVIRONMENT_VARIABLES.md for more information.'
        )
      }
    }

    return defaultUrl
  }

  // Production requires configuration
  throw new Error(
    'API_BASE_URL is required in production. ' +
      'Set it via window.__APP_CONFIG__.API_BASE_URL (runtime) or VITE_API_BASE_URL (build-time). ' +
      'See docs/ENVIRONMENT_VARIABLES.md for more information.'
  )
}

// Lazy getter to ensure runtime config is available when accessed
// This is important because window.__APP_CONFIG__ might not be set when the module loads
let _apiBaseUrl = null
const getApiBaseUrlLazy = () => {
  if (_apiBaseUrl === null) {
    _apiBaseUrl = getApiBaseUrl()
    if (typeof window !== 'undefined' && !window.__APP_CONFIG__ && !import.meta.env.DEV) {
      console.warn('No runtime config found, using build-time API base URL')
    }
  }
  return _apiBaseUrl
}

// Export as a getter object that converts to string when used
// This ensures the URL is resolved when actually used, not at module load time
export const API_BASE_URL = {
  toString: () => getApiBaseUrlLazy(),
  valueOf: () => getApiBaseUrlLazy(),
  [Symbol.toPrimitive]: () => getApiBaseUrlLazy(),
}

/**
 * API timeout constants for different request types
 * @type {Object}
 */
export const API_TIMEOUTS = {
  DEFAULT: 30000, // 30 seconds (existing API_TIMEOUT)
  ANALYTICS_NORMAL: 60000, // 1 minute
  ANALYTICS_LARGE_RANGE: 180000, // 3 minutes (increased for very slow requests)
  PRODUCTIVITY: 30000, // 30 seconds
  VEHICLE_DETAILS: 60000, // 60 seconds (for slow vehicle details endpoint)
}

/**
 * Productivity hours scaling — legacy workaround for the backend's former
 * under-count in /analytics/productivity/over-time (see
 * docs/backend-bug-productivity-over-time-undercounting.md).
 *
 * The backend fix (project-pulse-backend 4010336) ships alongside this
 * build, so scaling is OFF by default; it would over-correct against the
 * fixed backend. Rollback hatch only: PRODUCTIVITY_HOURS_SCALING: 'true'
 * in window.__APP_CONFIG__ (no rebuild) or VITE_PRODUCTIVITY_HOURS_SCALING
 * =true at build re-enables it if the backend is rolled back. Delete the
 * flag and the scaling once the deploy is verified.
 */
export const isProductivityHoursScalingEnabled = () =>
  getConfigValue('VITE_PRODUCTIVITY_HOURS_SCALING', 'PRODUCTIVITY_HOURS_SCALING') === 'true'

/**
 * API retry configuration constants
 * @type {Object}
 */
export const API_RETRY = {
  INITIAL_DELAY: 1000, // 1 second
  MAX_DELAY: 10000, // 10 seconds
  // The React Query layer owns retries (shouldRetryError); the fetch layer
  // only times out. Imperative callers can opt in via options.maxRetries.
  MAX_RETRIES: 0,
  RETRYABLE_STATUS_CODES: [408, 429, 502, 503, 504], // HTTP status codes that should retry
}

/**
 * Whether API retry is enabled by default
 * @type {boolean}
 */
export const API_RETRY_ENABLED = true

/**
 * AWS Cognito configuration
 *
 * Requirement 4: Environment Variables
 * All Cognito credentials are now loaded from environment variables or runtime config for security.
 * Required configuration (can be set via runtime config or environment variables):
 * - COGNITO_REGION (or VITE_COGNITO_REGION)
 * - COGNITO_USER_POOL_ID (or VITE_COGNITO_USER_POOL_ID)
 * - COGNITO_USER_POOL_WEB_CLIENT_ID (or VITE_COGNITO_USER_POOL_WEB_CLIENT_ID)
 *
 * Fallback values are provided for development convenience only.
 * In production: All configuration values are required (via runtime config or build-time env vars).
 */
const getCognitoConfig = () => {
  // Try runtime config first, then environment variables
  const region = getConfigValue('VITE_COGNITO_REGION', 'COGNITO_REGION')
  const userPoolId = getConfigValue('VITE_COGNITO_USER_POOL_ID', 'COGNITO_USER_POOL_ID')
  const userPoolWebClientId = getConfigValue(
    'VITE_COGNITO_USER_POOL_WEB_CLIENT_ID',
    'COGNITO_USER_POOL_WEB_CLIENT_ID'
  )

  // In development the values come from .env (copy .env.example to get started)
  if (import.meta.env.DEV) {
    if (!userPoolId || !userPoolWebClientId) {
      throw new Error(
        'Cognito configuration is missing. Copy .env.example to .env and fill in ' +
          'VITE_COGNITO_REGION, VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_USER_POOL_WEB_CLIENT_ID.'
      )
    }
    return {
      region: region || 'us-west-1',
      userPoolId,
      userPoolWebClientId,
      mandatorySignIn: true,
      authenticationFlowType: 'USER_SRP_AUTH',
    }
  }

  // Production requires all configuration values
  if (!region) {
    throw new Error(
      'COGNITO_REGION is required in production. ' +
        'Set it via window.__APP_CONFIG__.COGNITO_REGION (runtime) or VITE_COGNITO_REGION (build-time). ' +
        'See docs/ENVIRONMENT_VARIABLES.md for more information.'
    )
  }
  if (!userPoolId) {
    throw new Error(
      'COGNITO_USER_POOL_ID is required in production. ' +
        'Set it via window.__APP_CONFIG__.COGNITO_USER_POOL_ID (runtime) or VITE_COGNITO_USER_POOL_ID (build-time). ' +
        'See docs/ENVIRONMENT_VARIABLES.md for more information.'
    )
  }
  if (!userPoolWebClientId) {
    throw new Error(
      'COGNITO_USER_POOL_WEB_CLIENT_ID is required in production. ' +
        'Set it via window.__APP_CONFIG__.COGNITO_USER_POOL_WEB_CLIENT_ID (runtime) or VITE_COGNITO_USER_POOL_WEB_CLIENT_ID (build-time). ' +
        'See docs/ENVIRONMENT_VARIABLES.md for more information.'
    )
  }

  return {
    region,
    userPoolId,
    userPoolWebClientId,
    mandatorySignIn: true,
    authenticationFlowType: 'USER_SRP_AUTH',
  }
}

export const COGNITO_CONFIG = getCognitoConfig()
